import { Fetcher } from '@this/src/util';
import type {
  Blocker,
  BlockerCategory,
  BlockerType,
  ConversationState,
  DecidedField,
  SearchItem
} from '../types/conversationState';

type BlockerResponse = {
  key: string;
  category: BlockerCategory;
  type: BlockerType;
  raw?: string;
  assumed_date?: string;
  message?: string;
};

type DecidedFieldResponse = {
  key: string;
  category: BlockerCategory;
  label: string;
  value: string;
};

type ConversationStateResponse = {
  current_search_params: {
    items?: Record<string, SearchItem>;
    search_targets?: string[];
  };
  missing_fields: string[];
  date_validation_errors: string[];
  search_targets: string[];
  ready_to_search: boolean;
  updated_at: string | null;
  blockers: BlockerResponse[];
  decided_fields: DecidedFieldResponse[];
};

const toBlocker = (response: BlockerResponse): Blocker => ({
  key: response.key,
  category: response.category,
  type: response.type,
  raw: response.raw,
  assumedDate: response.assumed_date,
  message: response.message
});

const toDecidedField = (response: DecidedFieldResponse): DecidedField => ({
  key: response.key,
  category: response.category,
  label: response.label,
  value: response.value
});

const toConversationState = (response: ConversationStateResponse): ConversationState => ({
  currentSearchParams: {
    items: response.current_search_params?.items,
    searchTargets: response.current_search_params?.search_targets
  },
  missingFields: response.missing_fields,
  dateValidationErrors: response.date_validation_errors,
  searchTargets: response.search_targets,
  readyToSearch: response.ready_to_search,
  updatedAt: response.updated_at,
  blockers: (response.blockers ?? []).map(toBlocker),
  decidedFields: (response.decided_fields ?? []).map(toDecidedField)
});

export const fetchConversationState = async (tripId: string): Promise<ConversationState> => {
  const response = await Fetcher.get<ConversationStateResponse>(`/trips/${tripId}/ai_conversation_state`);
  return toConversationState(response);
};
